import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "@store/index";

import type {
  RequestShippingCasesPricesByIdParams,
  ShippingCasesPricesByIdResponse,
  ShippingCasesPricesCustomersResponse,
  EditShippingCasesByPricesIdRequest,
  RequestShippingCasesPricesCustomersParams,
  ShippingCasesPricesByIdItem,
  ShippingCasesPricesByIdStatus,
  SaveManageServicesRequest,
  ModifyPricePayload,
  EditShippingCasesByPricesIdResponse,
} from "./types";

import {
  cancelShippingCasesByPricesIdApi,
  editShippingCasesByPricesIdApi,
  getShippingCasesPricesByIdApi,
  getShippingCasesPricesCustomersApi,
  modifySelectedServicePriceApi,
  restoreServiceApi,
  saveManageServicesApi,
  sendDataInvoiceApi,
} from "./api";
import { SingleServiceType, SupplierPriceType } from "@pages/offerCreate/types";

type ViewState<T> = {
  items: T;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type WarningEntityKey =
  | "cancelShippingCasesByPricesId"
  | "restoreService"
  | "invoice";

type IncomeStatementState = {
  shippingCasesPricesById: ViewState<ShippingCasesPricesByIdResponse | null>;
  shippingCasesPricesCustomers: ViewState<
    ShippingCasesPricesCustomersResponse[]
  >;
  cancelShippingCasesByPricesId: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
  restoreService: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
  invoice: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
  editShippingCasesByPricesId: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
  modifySelectedServicePrice: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
  manageServices: {
    services: ViewState<
      (ShippingCasesPricesByIdItem & {
        isAdded?: boolean;
        isEdited?: boolean;
        offer_id?: number;
        status?: keyof typeof ShippingCasesPricesByIdStatus;
      })[]
    >;
  };
  addAssignmentModal: {
    open: boolean;
    type:
      | null
      | "addServiceItem"
      | "handleChooseService"
      | "addPackageServiceItem";
  };
};

const getInitialState = <T>(items: T): ViewState<T> => ({
  items,
  loading: true,
  error: null,
  success: null,
});

const initialState: IncomeStatementState = {
  shippingCasesPricesById: {
    ...getInitialState<ShippingCasesPricesByIdResponse | null>(null),
  },
  shippingCasesPricesCustomers: {
    ...getInitialState<ShippingCasesPricesCustomersResponse[]>([]),
  },
  cancelShippingCasesByPricesId: {
    loading: false,
    error: null,
    success: null,
  },
  restoreService: {
    loading: false,
    error: null,
    success: null,
  },
  editShippingCasesByPricesId: {
    loading: false,
    error: null,
    success: null,
  },
  modifySelectedServicePrice: {
    loading: false,
    error: null,
    success: null,
  },
  manageServices: {
    services: {
      ...getInitialState<ShippingCasesPricesByIdItem[]>([]),
    },
  },
  addAssignmentModal: {
    open: false,
    type: null,
  },
  invoice: {
    loading: false,
    error: null,
    success: null,
  },
};

const incomeStatementSlice = createSlice({
  name: "incomeStatement",
  initialState,
  reducers: {
    getShippingCasesPricesByIdStart(state: IncomeStatementState) {
      state.shippingCasesPricesById.loading = true;
      state.shippingCasesPricesById.error = null;
    },
    getShippingCasesPricesByIdSuccess(
      state: IncomeStatementState,
      action: PayloadAction<{ response: ShippingCasesPricesByIdResponse }>,
    ) {
      state.shippingCasesPricesById.items = action.payload.response;
      state.shippingCasesPricesById.loading = false;
      state.shippingCasesPricesById.success = "Services fetched successfully";
      state.shippingCasesPricesById.error = null;
    },
    getShippingCasesPricesByIdFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.shippingCasesPricesById.loading = false;
      state.shippingCasesPricesById.error = action.payload;
    },
    cleanShippingCasesPricesById(state: IncomeStatementState) {
      state.shippingCasesPricesById.items = null;
      state.shippingCasesPricesById.loading = false;
      state.shippingCasesPricesById.error = null;
    },

    getShippingCasesPricesCustomersStart(state: IncomeStatementState) {
      state.shippingCasesPricesCustomers.loading = true;
      state.shippingCasesPricesCustomers.error = null;
    },
    getShippingCasesPricesCustomersSuccess(
      state: IncomeStatementState,
      action: PayloadAction<{
        response: ShippingCasesPricesCustomersResponse[];
      }>,
    ) {
      state.shippingCasesPricesCustomers.items = action.payload.response;
      state.shippingCasesPricesCustomers.loading = false;
      state.shippingCasesPricesCustomers.success =
        "Customers fetched successfully";
      state.shippingCasesPricesCustomers.error = null;
    },
    getShippingCasesPricesCustomersFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.shippingCasesPricesCustomers.loading = false;
      state.shippingCasesPricesCustomers.error = action.payload;
    },
    cleanShippingCasesPricesCustomers(state: IncomeStatementState) {
      state.shippingCasesPricesCustomers.items = [];
      state.shippingCasesPricesCustomers.loading = false;
      state.shippingCasesPricesCustomers.error = null;
    },
    cancelShippingCasesByPricesIdStart(state: IncomeStatementState) {
      state.cancelShippingCasesByPricesId.loading = true;
      state.cancelShippingCasesByPricesId.error = null;
    },
    cancelShippingCasesByPricesIdSuccess(state: IncomeStatementState) {
      state.cancelShippingCasesByPricesId.loading = false;
      state.cancelShippingCasesByPricesId.success =
        "Service canceled successfully";
      state.cancelShippingCasesByPricesId.error = null;
    },
    cancelShippingCasesByPricesIdFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.cancelShippingCasesByPricesId.loading = false;
      state.cancelShippingCasesByPricesId.error = action.payload;
    },
    restoreServiceStart(state: IncomeStatementState) {
      state.restoreService.loading = true;
      state.restoreService.error = null;
    },
    restoreServiceSuccess(state: IncomeStatementState) {
      state.restoreService.loading = false;
      state.restoreService.success = "Service restored successfully";
      state.restoreService.error = null;
    },
    restoreServiceFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.restoreService.loading = false;
      state.restoreService.error = action.payload;
    },
    editShippingCasesByPricesIdStart(state: IncomeStatementState) {
      state.editShippingCasesByPricesId.loading = true;
      state.editShippingCasesByPricesId.error = null;
    },
    editShippingCasesByPricesIdSuccess(state: IncomeStatementState) {
      state.editShippingCasesByPricesId.loading = false;
      state.editShippingCasesByPricesId.success =
        "Service updated successfully";
      state.editShippingCasesByPricesId.error = null;
    },
    editShippingCasesByPricesIdFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.editShippingCasesByPricesId.loading = false;
      state.editShippingCasesByPricesId.error = action.payload;
    },

    modifySelectedServicePriceStart(state: IncomeStatementState) {
      state.modifySelectedServicePrice.loading = true;
      state.modifySelectedServicePrice.error = null;
    },
    modifySelectedServicePriceSuccess(state: IncomeStatementState) {
      state.modifySelectedServicePrice.loading = false;
      state.modifySelectedServicePrice.success =
        "Service price modified successfully";
      state.modifySelectedServicePrice.error = null;
    },
    modifySelectedServicePriceFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.modifySelectedServicePrice.loading = false;
      state.modifySelectedServicePrice.error = action.payload;
    },

    // Manage Services
    getManageServicesStart(state: IncomeStatementState) {
      state.manageServices.services.loading = true;
      state.manageServices.services.error = null;
    },
    getManageServicesSuccess(
      state: IncomeStatementState,
      action: PayloadAction<{
        response: ShippingCasesPricesByIdResponse;
      }>,
    ) {
      state.manageServices.services.items = action.payload.response.items;
      state.manageServices.services.loading = false;
      state.manageServices.services.success = "Services fetched successfully";
      state.manageServices.services.error = null;
    },
    getManageServicesFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.manageServices.services.loading = false;
      state.manageServices.services.error = action.payload;
    },
    cleanManageServices(state: IncomeStatementState) {
      state.manageServices.services.items = [];
      state.manageServices.services.loading = false;
    },
    removeManageServicesItem(
      state: IncomeStatementState,
      action: PayloadAction<number>,
    ) {
      state.manageServices.services.items =
        state.manageServices.services.items.filter(
          (service) => service.id !== action.payload,
        );
    },

    removeManageServicesItemSubService(
      state: IncomeStatementState,
      action: PayloadAction<{ parentId: number; id: number }>,
    ) {
      state.manageServices.services.items =
        state.manageServices.services.items.map((service) => {
          if (service.id !== action.payload.parentId) return service;

          return {
            ...service,
            passive_prices:
              service.passive_prices?.filter(
                (p) => p.id !== action.payload.id,
              ) ?? [],
          };
        });
    },
    updateSelectedServicesOrder(
      state: IncomeStatementState,
      action: PayloadAction<ShippingCasesPricesByIdItem[]>,
    ) {
      state.manageServices.services.items = action.payload;
    },
    addServiceItem(
      state: IncomeStatementState,
      action: PayloadAction<{
        selected: SingleServiceType;
        selectedSuppliers: SupplierPriceType[];
        customer: { id: number; name: string };
        office: { id: number; name: string };
      }>,
    ) {
      const { selected, selectedSuppliers, customer, office } = action.payload;

      const generatedItem = {
        id: Math.floor(Math.random() * 1000) ** 3,
        isAdded: true,
        active_price: selected,
        passive_prices: selectedSuppliers,
        prices:
          selected?.price_data.data.reduce<number[]>((acc, item) => {
            if (item.price != null) acc.push(Number(item.price));
            return acc;
          }, []) || [],
        expiration_date: selected?.expiration_date,
        // TODO: confirm this fields
        offer_id: 1,
        status: "NEW" as const,
        office: office,
        customer: customer,
        invoice_status: null,
        invoice_frequency: null,
        active_price_editable: false,
      };
      state.manageServices.services.items = [
        ...state.manageServices.services.items,
        generatedItem,
      ];
    },

    addPackageServiceItem(
      state: IncomeStatementState,
      action: PayloadAction<{
        selected: SingleServiceType[];
        customer: { id: number; name: string };
        office: { id: number; name: string };
      }>,
    ) {
      const { selected, customer, office } = action.payload;

      const generatedItems = selected.map((service) => ({
        id: Math.floor(Math.random() * 1000),
        isAdded: true,
        active_price: service,
        passive_prices: [],
        prices:
          service?.price_data.data.reduce<number[]>((acc, item) => {
            if (item.price != null) acc.push(Number(item.price));
            return acc;
          }, []) || [],
        comment: service?.comment ?? "",
        expiration_date: service?.expiration_date ?? null,
        office,
        customer,
        // TODO: confirm this fields
        offer_id: 1,
        status: "NEW" as const,
        invoice_status: null,
        invoice_frequency: null,
        active_price_editable: false,
      }));

      state.manageServices.services.items = [
        ...state.manageServices.services.items,
        ...generatedItems,
      ];
    },

    editSelectedSuppliers(
      state: IncomeStatementState,
      action: PayloadAction<{
        id: number;
        selectedSuppliers: SupplierPriceType[];
      }>,
    ) {
      state.manageServices.services.items =
        state.manageServices.services.items.map((service) => {
          if (service.id !== action.payload.id) return service;

          return {
            ...service,
            passive_prices: action.payload.selectedSuppliers,
            isEdited: true,
          };
        });
    },

    editSelectedService(
      state: IncomeStatementState,
      action: PayloadAction<{
        parentId: number;
        row: SingleServiceType;
      }>,
    ) {
      const { parentId, row } = action.payload;
      const prices = state.manageServices.services.items as any;

      if (prices) {
        const parent = prices.find((price: any) => price.id === parentId);

        if (parent && parent.active_price?.id === row.id) {
          parent.active_price = row;

          parent.prices =
            row.price_data?.data?.reduce<number[]>((acc, item) => {
              if (item.price != null) {
                acc.push(Number(item.price));
              }
              return acc;
            }, []) || [];

          parent.isEdited = true;
        }
      }
    },

    editSelectedAssignment(
      state: IncomeStatementState,
      action: PayloadAction<{
        id: number;
        customer: { id: number; name: string };
        office: { id: number; name: string };
      }>,
    ) {
      state.manageServices.services.items =
        state.manageServices.services.items.map((service) => {
          if (service.id !== action.payload.id) return service;

          return {
            ...service,
            isEdited: true,
            customer: action.payload.customer,
            office: action.payload.office,
          };
        });
    },
    openAddAssignmentModal(
      state: IncomeStatementState,
      action: PayloadAction<{
        type:
          | "addServiceItem"
          | "handleChooseService"
          | "addPackageServiceItem";
      }>,
    ) {
      state.addAssignmentModal.open = true;
      state.addAssignmentModal.type = action.payload.type;
    },
    closeAssignmentModal(state: IncomeStatementState) {
      state.addAssignmentModal.open = false;
      state.addAssignmentModal.type = null;
    },

    saveManageServicesStart(state: IncomeStatementState) {
      state.manageServices.services.loading = true;
      state.manageServices.services.error = null;
    },

    saveManageServicesSuccess(state: IncomeStatementState) {
      state.manageServices.services.loading = false;
      state.manageServices.services.success = "Services saved successfully";
      state.manageServices.services.error = null;
    },

    saveManageServicesFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.manageServices.services.loading = false;
      state.manageServices.services.error = action.payload;
    },
    hideWarning(state: IncomeStatementState) {
      state.manageServices.services.error = null;
      state.manageServices.services.success = null;
    },
    hideWarningByEntity(
      state: IncomeStatementState,
      action: PayloadAction<WarningEntityKey>,
    ) {
      state[action.payload].error = null;
      state[action.payload].success = null;
    },
    hideAllWarnings(state: IncomeStatementState) {
      (Object.keys(state) as Array<keyof IncomeStatementState>).forEach(
        (key) => {
          if ("error" in state[key]) state[key].error = null;
          if ("success" in state[key]) state[key].success = null;
        },
      );
    },
    updateSelectedService(
      state: IncomeStatementState,
      action: PayloadAction<EditShippingCasesByPricesIdResponse>,
    ) {
      const item = state.shippingCasesPricesById.items?.items.find(
        (i) => i.id === action.payload.id,
      );
      if (item) {
        Object.assign(item, action.payload);
      }
    },
    sendInvoiceStart(state: IncomeStatementState) {
      state.invoice.loading = true;
      state.invoice.error = null;
    },
    sendInvoiceSuccess(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.invoice.loading = false;
      state.invoice.success = action.payload;
      state.invoice.error = null;
    },
    sendInvoiceFailure(
      state: IncomeStatementState,
      action: PayloadAction<string>,
    ) {
      state.invoice.loading = false;
      state.invoice.error = action.payload;
      state.invoice.success = null;
    },
  },
});

export const {
  getShippingCasesPricesByIdStart,
  getShippingCasesPricesByIdSuccess,
  getShippingCasesPricesByIdFailure,
  cleanShippingCasesPricesById,
  getShippingCasesPricesCustomersStart,
  getShippingCasesPricesCustomersSuccess,
  getShippingCasesPricesCustomersFailure,
  cleanShippingCasesPricesCustomers,
  cancelShippingCasesByPricesIdStart,
  cancelShippingCasesByPricesIdSuccess,
  cancelShippingCasesByPricesIdFailure,
  restoreServiceStart,
  restoreServiceSuccess,
  restoreServiceFailure,
  editShippingCasesByPricesIdStart,
  editShippingCasesByPricesIdSuccess,
  editShippingCasesByPricesIdFailure,
  getManageServicesStart,
  getManageServicesSuccess,
  getManageServicesFailure,
  cleanManageServices,
  removeManageServicesItem,
  removeManageServicesItemSubService,
  addServiceItem,
  editSelectedSuppliers,
  editSelectedAssignment,
  addPackageServiceItem,
  openAddAssignmentModal,
  closeAssignmentModal,
  editSelectedService,
  saveManageServicesStart,
  saveManageServicesSuccess,
  saveManageServicesFailure,
  hideWarning,
  hideWarningByEntity,
  updateSelectedServicesOrder,
  modifySelectedServicePriceSuccess,
  modifySelectedServicePriceFailure,
  modifySelectedServicePriceStart,
  hideAllWarnings,
  updateSelectedService,
  sendInvoiceStart,
  sendInvoiceSuccess,
  sendInvoiceFailure,
} = incomeStatementSlice.actions;

export default incomeStatementSlice.reducer;

export const fetchShippingCasesPricesById =
  (id: number, params: RequestShippingCasesPricesByIdParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getShippingCasesPricesByIdStart());
      const services = await getShippingCasesPricesByIdApi(id, params);
      dispatch(getShippingCasesPricesByIdSuccess({ response: services }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getShippingCasesPricesByIdFailure(error));
    }
  };

export const fetchShippingCasesPricesCustomers =
  (id: number, params?: RequestShippingCasesPricesCustomersParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getShippingCasesPricesCustomersStart());
      const customers = await getShippingCasesPricesCustomersApi(id, params);
      dispatch(getShippingCasesPricesCustomersSuccess({ response: customers }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getShippingCasesPricesCustomersFailure(error));
    }
  };

export const cancelShippingCasesByPricesId =
  (sid: number, pricesId: number, callback: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(cancelShippingCasesByPricesIdStart());
      await cancelShippingCasesByPricesIdApi(sid, pricesId);
      if (callback) {
        callback();
      }
      dispatch(cancelShippingCasesByPricesIdSuccess());
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(cancelShippingCasesByPricesIdFailure(error));
    }
  };

export const restoreService =
  (sid: number, serviceId: number, callback: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(restoreServiceStart());
      await restoreServiceApi(sid, serviceId);
      if (callback) {
        callback();
      }
      dispatch(restoreServiceSuccess());
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(restoreServiceFailure(error));
    }
  };

export const editShippingCasesByPricesId =
  (
    sid: number,
    pricesId: number,
    payload: Partial<EditShippingCasesByPricesIdRequest>,
    callback?: () => void,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(editShippingCasesByPricesIdStart());
      const data = await editShippingCasesByPricesIdApi(sid, pricesId, payload);
      if (callback) {
        callback?.();
      } else {
        dispatch(updateSelectedService(data));
      }
      dispatch(editShippingCasesByPricesIdSuccess());
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(editShippingCasesByPricesIdFailure(error));
    }
  };

export const fetchManageServices =
  (id: number, params: RequestShippingCasesPricesByIdParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getManageServicesStart());
      const services = await getShippingCasesPricesByIdApi(id, params);
      dispatch(getManageServicesSuccess({ response: services }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getManageServicesFailure(error));
    }
  };

export const saveManageServices =
  (
    id: number,
    payload: SaveManageServicesRequest,
    callback: () => void,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(saveManageServicesStart());
      await saveManageServicesApi(id, payload);
      dispatch(saveManageServicesSuccess());
      dispatch(cleanManageServices());
      if (callback) {
        callback();
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(saveManageServicesFailure(error));
    }
  };

export const modifySelectedServicePrice =
  (
    sid: number,
    pricesId: number,
    payload: ModifyPricePayload,
    callback: () => void,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(modifySelectedServicePriceStart());
      await modifySelectedServicePriceApi(sid, pricesId, payload);
      if (callback) {
        callback();
      }
      dispatch(modifySelectedServicePriceSuccess());
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(modifySelectedServicePriceFailure(error));
    }
  };

export const sendDataInvoice =
  (sid: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(sendInvoiceStart());
      await sendDataInvoiceApi(sid);
      dispatch(sendInvoiceSuccess("Data sent to the Rule Engine"));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(sendInvoiceFailure(error));
    }
  };
